import React, { ComponentProps, useRef, useState } from 'react';
import { AppLibraryEditableElement } from '../AppLibraryTypes';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { AppLibraryDeleteForm } from '../partials/AppLibraryDeleteForm';
import { AppLibraryAddOrUpdateForm } from '../partials/AppLibraryAddOrUpdateForm';
import IForm from '../../../types/IForm';
import { asFetchableView, useRefreshView } from '../../../_lib/ViewFetcher';
import { DraggableAppLibraryListItem } from '../partials/AppLibraryListItem';
import { SortableContentList } from '../../../_lib/components/SortableContentList';
import { useArrayEffect } from '../../../_lib/components/SortableContentList/SortableContentList';
import { useSonTranslation } from '../../../translation/useSonTranslation';

type AppLibraryEditableListProps = {
  mandatoryItems: AppLibraryEditableElement[];
  optionalItems: AppLibraryEditableElement[];
  create: IForm;
  updateOrder: IForm;
};

export function AppLibraryEdit({ mandatoryItems, optionalItems, create, updateOrder }: AppLibraryEditableListProps) {
  const { t } = useSonTranslation('AppLibrary');
  const ref = useRef<HTMLButtonElement>(null);

  const { delayedRefreshView } = useRefreshView();

  const [activeForm, setActiveForm] = useState<{ type: 'add' | 'edit' | 'delete'; form: IForm } | null>(null);
  const items = mandatoryItems.concat(optionalItems);

  /**
   * After successfully changes, modal should be closed
   * */
  useArrayEffect(() => setActiveForm(null), items);

  const closeModal = () => setActiveForm(null);
  const openAddForm = () => setActiveForm({ type: 'add', form: create });
  const openEditForm = (form: IForm) => setActiveForm({ type: 'edit', form });
  const openDeleteForm = (form: IForm) => setActiveForm({ type: 'delete', form });

  const switchActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} headline={t('removeLink')} focusOnReturn={ref} initialFocusIndex={1}>
            <AppLibraryDeleteForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addLink')} initialFocusIndex={2}>
            <AppLibraryAddOrUpdateForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} headline={t('editLink')} initialFocusIndex={2}>
            <AppLibraryAddOrUpdateForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar
        heading={t('addLink')}
        hierarchy={3}
        modifier={items.length > 0 ? ['border-bottom', 'border-top'] : 'border-top'}
      >
        <BlockBarButton ref={ref} onClick={openAddForm} text={t('addLink')} svgSpriteId={Icons.ADD_PLUS} />
      </BlockBar>
      {mandatoryItems.length > 0 && (
        <>
          <div className="app-library__list-head-container">
            <h4 className="app-library__list-head-text">{t('listHeader.mandatoryLinks')}</h4>
          </div>
          <SortableContentList
            id="app-library-list-mandatory"
            items={mandatoryItems}
            form={updateOrder}
            hasDarkDragLayerBackground
          >
            {item => (
              <DraggableAppLibraryListItem
                key={item.id}
                {...item}
                openDeleteForm={openDeleteForm}
                openEditForm={openEditForm}
              />
            )}
          </SortableContentList>
        </>
      )}
      {optionalItems.length > 0 && (
        <>
          <div className="app-library__list-head-container">
            <h4 className="app-library__list-head-text">{t('listHeader.optionalLinks')}</h4>
          </div>
          <SortableContentList
            id="app-library-list-optional"
            items={optionalItems}
            form={updateOrder}
            hasDarkDragLayerBackground
          >
            {item => (
              <DraggableAppLibraryListItem
                key={item.id}
                {...item}
                openDeleteForm={openDeleteForm}
                openEditForm={openEditForm}
              />
            )}
          </SortableContentList>
        </>
      )}
      {switchActiveForm()}
    </>
  );
}

export const AppLibraryEditView = asFetchableView(AppLibraryEdit);
export type AppLibraryEditView = ComponentProps<typeof AppLibraryEditView>;
